// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.43.0 (owner ruling OR-26, AC2) — which optional overlays this
 * organisation runs, for Module 30's menu and Overview page.
 *
 * One read of `GET /api/v1/sector-overlays/navigation` per signed-in session,
 * held in a module-level cache that every mounted instance shares, and read
 * again only through `refresh` / `refreshSectorOverlayNavigation` (the
 * installer calls it after an install, a mode change or an uninstall, so the
 * sidebar follows without a reload).
 *
 * - **Not read when the module is off.** On Light and Standard the
 *   `sector_overlays` module toggle is off (written by the package enforcement)
 *   and the endpoint answers `403 PACKAGE_LIMIT`, so the hook waits for the
 *   module configuration and reads nothing while the module is disabled.
 * - **Failure is logged, never shown.** A failed first read leaves `overlays`
 *   `null` and `error` set (through `extractErrorMessage`); the sidebar then
 *   shows no sector leaf and the installer stays reachable. A failed refresh
 *   keeps the last good `overlays` and sets only `error` (gate E12.5 pass 1,
 *   P3-2), so a passing network fault does not take the sector leaves away. A
 *   `PACKAGE_LIMIT` refusal is the server saying "not in your package", not a
 *   failure: it is not logged, it drops any earlier answer (pass 2), and it is
 *   published as `notEntitled: true` with `error: null` (fix-sprint-20261102-0448
 *   F5). The projection is the server's own entitlement answer and every
 *   signed-in user may read it, so the menu and the Overview follow it even
 *   where the module toggle cannot be read (a role without
 *   `platform:module-config:read` gets 403 from `/module-config`, and every
 *   module then reads as enabled).
 * - **Signing out forgets the answer.** The cache is forgotten when the last
 *   consumer unmounts (every sign-out path unmounts the protected tree) and
 *   when a mounted consumer sees nobody signed in, so the next sign-in, the
 *   same user's included, reads afresh.
 * - **Not paginated.** The answer is a plain object `{ entitled, overlays }`;
 *   `overlays` is read directly (no R-23 `data` unwrap).
 *
 * `operationalOverlayViewIds` turns the answer into the registry views an
 * operational overlay declares into, through the same join the Overlay
 * Catalog's Feature views modal uses (`viewsForDeclaredSurfaces`). A reference,
 * superseded or uninstalled overlay contributes nothing.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { ModuleId } from '@vn-grc/shared';
import { apiClient, type SectorOverlayNavigationEntry } from '../services/api-client.js';
import { isPackageLimitError } from '../services/api-error.js';
import { extractErrorMessage } from '../utils/api-helpers.js';
import { userRecordIdOf } from '../utils/user-record-id.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useModuleConfig } from '../contexts/ModuleConfigContext.js';
import { viewsForDeclaredSurfaces } from '../config/overlay-register-surfaces.js';

export type { SectorOverlayNavigationEntry } from '../services/api-client.js';

export interface SectorOverlayNavigationResult {
  /** The installed overlays with their state; `null` while loading or after a failed read. */
  overlays: SectorOverlayNavigationEntry[] | null;
  loading: boolean;
  /** The failed read's message (`extractErrorMessage`); `null` otherwise. Not for display. */
  error: string | null;
  /**
   * The server answered `403 PACKAGE_LIMIT`: Sector Overlays is not in this
   * installation's package. Not a failure (`error` stays `null`).
   */
  notEntitled: boolean;
  /** Read the projection again and notify every mounted instance. */
  refresh: () => Promise<void>;
}

interface Snapshot {
  overlays: SectorOverlayNavigationEntry[] | null;
  loading: boolean;
  error: string | null;
  notEntitled: boolean;
}

const EMPTY: Snapshot = { overlays: null, loading: false, error: null, notEntitled: false };
const READ_FAILED = 'The sector overlay navigation could not be read.';

let snapshot: Snapshot = EMPTY;
/** Whose answer the cache holds (organisation and signed-in user). */
let cacheKey: string | null = null;
/** A read has been asked for under `cacheKey` (so `refresh` may read again). */
let requested = false;
/** Bumped by every read and every reset; a read whose generation is stale is dropped. */
let generation = 0;
const listeners = new Set<() => void>();

function publish(next: Snapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

/**
 * Forget whose answer the cache holds and drop any read in flight, so the next
 * consumer to mount reads afresh — the same user's next session included.
 */
export function forgetSectorOverlayNavigation(): void {
  generation += 1;
  requested = false;
  cacheKey = null;
  publish(EMPTY);
}

/**
 * Gate E12.5 pass 2 (P3-1) — the cache lives exactly as long as some consumer
 * is mounted. Signing out (`logout`, the shared 401 handler, an expired
 * session) never renders a consumer with a null user: `ProtectedRoute` sends
 * the person to /login and every consumer unmounts. So the cache is forgotten
 * when the LAST listener leaves, which every one of those paths goes through
 * and none of them has to know about; the sidebar stays mounted across admin
 * navigation, so a page consumer coming and going keeps it.
 */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) forgetSectorOverlayNavigation();
  };
}

function getSnapshot(): Snapshot {
  return snapshot;
}

/** Start one read, superseding any read in flight; resolves when it settles. */
function read(): Promise<void> {
  requested = true;
  const mine = ++generation;
  publish({ ...snapshot, loading: true });
  return (async () => {
    try {
      const res = await apiClient.getSectorOverlayNavigation();
      if (mine !== generation) return;
      publish({
        overlays: Array.isArray(res?.overlays) ? res.overlays : [],
        loading: false,
        error: null,
        notEntitled: false,
      });
    } catch (err) {
      if (mine !== generation) return;
      if (isPackageLimitError(err)) {
        // A package refusal is a final answer — not in this package — and drops
        // any earlier answer (pass 2, P3-2b). It is no failure: `error` is null
        // (fix-sprint-20261102-0448 F5).
        publish({ overlays: null, loading: false, error: null, notEntitled: true });
        return;
      }
      console.warn('[sector-overlays] navigation projection could not be read', err);
      // A passing fault keeps the last good answer (null when there was none),
      // so a failed refresh does not take the sector leaves away (pass 1,
      // P3-2).
      publish({
        overlays: snapshot.overlays,
        loading: false,
        error: extractErrorMessage(err, READ_FAILED),
        notEntitled: false,
      });
    }
  })();
}

/**
 * Read the projection again once and notify every mounted instance — call it
 * after an install, a mode change or an uninstall. When no instance has asked
 * for a read yet (the module is off, or nothing is mounted), nothing is read:
 * the next instance that may read reads afresh.
 */
export function refreshSectorOverlayNavigation(): Promise<void> {
  if (!requested) {
    generation += 1;
    publish(EMPTY);
    return Promise.resolve();
  }
  return read();
}

/** Test seam: forget the cached answer and every read in flight. */
export function __resetSectorOverlayNavigationCache(): void {
  generation += 1;
  requested = false;
  cacheKey = null;
  snapshot = EMPTY;
  listeners.forEach((listener) => listener());
}

/**
 * The ids of the registry views (`OVERLAY_REGISTER_VIEWS`) that at least one
 * overlay in state `operational` declares into — the same join
 * `deriveOverlayViewRows` makes for one overlay. Reference and superseded
 * overlays contribute nothing; a view several overlays declare into is one id.
 */
export function operationalOverlayViewIds(
  overlays: readonly SectorOverlayNavigationEntry[],
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const overlay of overlays) {
    if (overlay.state !== 'operational') continue;
    const { views } = viewsForDeclaredSurfaces(overlay.register_surfaces ?? [], {
      pack_declarations: overlay.pack_declarations,
    });
    for (const view of views) ids.add(view.id);
  }
  return ids;
}

/**
 * Whether the `sector_overlays` module is on, and whether the configuration has
 * answered. Without a `ModuleConfigProvider` (a component rendered on its own)
 * it reads as on and answered, as `useSectorOverlaysEntitled` does.
 */
function useModuleGate(): { enabled: boolean; waiting: boolean } {
  try {
    // `useModuleConfig` calls `useContext` unconditionally before it throws, so
    // the hook order is the same on every render.
    const config = useModuleConfig();
    const enabled =
      typeof config?.isModuleEnabled === 'function'
        ? config.isModuleEnabled(ModuleId.SECTOR_OVERLAYS)
        : true;
    return { enabled, waiting: config?.configState === 'loading' };
  } catch {
    return { enabled: true, waiting: false };
  }
}

/**
 * The organisation and user the answer belongs to: `null` while nobody is
 * signed in (nothing may be read), `''` without an `AuthProvider`.
 */
function useSessionKey(): string | null {
  try {
    const { user } = useAuth();
    if (!user) return null;
    // The person's id through `userRecordIdOf` (Sprint 63.26.0 R1), never `user_id`.
    return `${user.org_id ?? ''}:${userRecordIdOf(user) ?? ''}`;
  } catch {
    return '';
  }
}

export function useSectorOverlayNavigation(): SectorOverlayNavigationResult {
  const { enabled, waiting } = useModuleGate();
  const sessionKey = useSessionKey();
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const mayRead = enabled && !waiting && sessionKey !== null;

  // Gate E12.5 pass 1 (P3-2) — signed out: forget whose answer this was, so a
  // sign-in without a reload (the same user's too) reads afresh rather than
  // finding `requested` set and the earlier session's answer in the cache.
  // In the app every consumer unmounts on sign-out (see `subscribe`); this
  // covers a consumer that stays mounted while the session ends.
  useEffect(() => {
    if (sessionKey !== null || cacheKey === null) return;
    forgetSectorOverlayNavigation();
  }, [sessionKey]);

  useEffect(() => {
    if (!mayRead) return;
    if (cacheKey !== sessionKey) {
      // Another organisation or user: the cached answer is not theirs.
      generation += 1;
      requested = false;
      cacheKey = sessionKey;
      publish(EMPTY);
    }
    if (!requested) void read();
  }, [mayRead, sessionKey]);

  const refresh = useCallback(() => refreshSectorOverlayNavigation(), []);

  if (!enabled || sessionKey === null)
    return { overlays: null, loading: false, error: null, notEntitled: false, refresh };
  // Until this session's read has been asked for, the cache holds nobody's answer.
  const unanswered = cacheKey !== sessionKey || !requested;
  return {
    overlays: unanswered ? null : current.overlays,
    loading: unanswered || current.loading,
    error: unanswered ? null : current.error,
    notEntitled: unanswered ? false : current.notEntitled,
    refresh,
  };
}
